import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Stack, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

const VideoViewer = ({ videoUrl, mimeType }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [error, setError] = useState(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [fileName, setFileName] = useState('video.mov');

  useEffect(() => {
    // Extract filename from URL if possible
    if (videoUrl) {
      const urlParts = videoUrl.split('/');
      const possibleFileName = urlParts[urlParts.length - 1];
      if (possibleFileName && possibleFileName.includes('.')) {
        setFileName(possibleFileName);
      }
    }

    // Reset states on new video
    setError(null);
    setIsPlayerReady(false);

    // Video.js options with extensive compatibility settings
    const videoJsOptions = {
      autoplay: false,
      controls: true,
      responsive: true,
      fluid: true,
      preload: 'auto',
      playbackRates: [0.5, 1, 1.5, 2],
      sources: [{
        src: videoUrl,
        type: mimeType || 'video/quicktime'
      }],
      // Add HTML5 tech configs to try multiple approaches
      html5: {
        vhs: {
          overrideNative: true,
          fastQualityChange: true,
        },
        nativeVideoTracks: false,
        nativeAudioTracks: false,
        nativeTextTracks: false
      },
      // Timeout settings
      liveui: false,
      inactivityTimeout: 0,
      // Responsive sizing
      aspectRatio: '16:9',
      // Performance settings
      techOrder: ['html5'],
      // Debug mode for console info
      debug: true
    };

    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      // Initialize Video.js player
      if (videoRef.current) {
        try {
          const player = videojs(videoRef.current, videoJsOptions, () => {
            console.log('Player initialized');
            playerRef.current = player;
            setIsPlayerReady(true);

            // Add error event listener
            player.on('error', () => {
              const e = player.error();
              console.error('Video.js error:', e);
              
              let errorMessage = 'Error loading video.';
              
              // Provide more specific error messages based on error code
              if (e && e.code) {
                switch (e.code) {
                  case 1: // MEDIA_ERR_ABORTED
                    errorMessage = 'The video playback was aborted.';
                    break;
                  case 2: // MEDIA_ERR_NETWORK
                    errorMessage = 'A network error caused the video download to fail.';
                    break;
                  case 3: // MEDIA_ERR_DECODE
                    errorMessage = 'The video format (.mov) is not supported by your browser.';
                    break;
                  case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
                    errorMessage = 'The video format (.mov) or MIME type is not supported by your browser.';
                    break;
                  default:
                    errorMessage = 'An unknown error occurred.';
                }
              }
              
              setError(errorMessage);
            });
          });
        } catch (e) {
          console.error('Error initializing video.js:', e);
          setError('Error initializing video player. The video format may not be supported.');
        }
      }
    } else {
      // If player already initialized, just update the source
      try {
        const player = playerRef.current;
        player.src([{
          src: videoUrl,
          type: mimeType || 'video/quicktime'
        }]);
        
        // Reset error state if previous video had errors
        setError(null);
        setIsPlayerReady(true);
      } catch (e) {
        console.error('Error updating video source:', e);
        setError('Error loading video. The format may not be supported by your browser.');
      }
    }

    // Cleanup function - dispose player on component unmount
    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.dispose();
        } catch (e) {
          console.error('Error disposing video player:', e);
        }
        playerRef.current = null;
      }
    };
  }, [videoUrl, mimeType]);

  const handleDownload = () => {
    // Create an anchor element and trigger download
    try {
      const a = document.createElement('a');
      a.href = videoUrl;
      a.download = fileName || 'video.mov'; // Use detected filename or default
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      console.error('Error downloading video:', e);
      alert('Error downloading video. Please try again.');
    }
  };

  return (
    <Box>
      {/* Download button always visible at the top */}
      <Box sx={{ mb: 2 }}>
        <Button 
          variant="contained" 
          startIcon={<DownloadIcon />} 
          onClick={handleDownload}
          color="primary"
          fullWidth
        >
          Download Video for Offline Viewing
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          MOV files often contain proprietary Apple QuickTime codecs that may not play in all browsers.
          Downloading the file will allow you to view it in your computer's media player.
        </Typography>
      </Box>

      {error ? (
        <Box
          sx={{
            p: 3,
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '4px',
            textAlign: 'center',
            height: '300px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            MOV files often use proprietary Apple QuickTime codecs that aren't supported in web browsers.
            We recommend downloading the file to view it in your local media player.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            '& .video-js': {
              width: '100%',
              height: '500px',
              backgroundColor: '#000',
            }
          }}
        >
          <div data-vjs-player>
            <video
              ref={videoRef}
              className="video-js vjs-big-play-centered vjs-fluid"
            />
          </div>
          
          {!isPlayerReady && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                zIndex: 1,
              }}
            >
              Loading video player...
            </Box>
          )}
        </Box>
      )}
      
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Note:</strong> MOV files are QuickTime videos that may have limited compatibility with web browsers.
          If the video doesn't play, please use the download button above to view it locally.
        </Typography>
      </Box>
    </Box>
  );
};

export default VideoViewer;